import React, { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../hooks/store';
import { Attendee, Request, Slot } from '../types';
import Modal from '../components/common/Modal';
import getFileURL from '../utils/getFileURL';
import { Form, Hidden, Input, Files, Fieldset } from '@alxgrn/react-form';
import { post } from '../utils/api';
import { requestsAdd, requestsDel } from '../store/requestsSlice';
import './RequestPost.css';
import Translate from '../components/common/Translate';

export interface RequestPostProps {
    request?: Request;
    slot?: Slot;
    to?: Attendee;
    isOpen: boolean;
    onClose: () => void;
};

const LIMIT = 500;

const RequestPost: FC<RequestPostProps> = ({ slot, to, request, isOpen, onClose }) => {
	const { t, i18n } = useTranslation();
    const lang = i18n.language === 'en' ? 'en' : 'ru';
	const name = i18n.language === 'en' ? 'name_en' : 'name_ru';
    const date_loc = i18n.language === 'en' ? 'date_en' : 'date_ru';
    const duration_loc = i18n.language === 'en' ? ' min' : ' мин';
    const dispatch = useAppDispatch();
    const [ date, setDate ] = useState('unknown');
    const [ time, setTime ] = useState('unknown');
    const [ duration, setDuration ] = useState('unknown');
    const [ error, setError ] = useState('');
    const [ success, setSuccess ] = useState(false);
    const [ isLoading, setIsLoading ] = useState(false);
	const [ message_ru, setMessageRu ] = useState('');
	const [ message_en, setMessageEn ] = useState('');
    const [ files, setFiles ] = useState<File[]>([]);
    const user = useAppSelector(store => store.auth.user);
    const event = useAppSelector(store => store.event);
    const [ canSend, setCanSend ] = useState(false);
    const [ isTranslating, setIsTranslating ] = useState(false);

    // Вычисляем можем ли еще отправлять запросы
    useEffect(() => {
        if(!user || !event) return;
        if(!user.is_approved) return; // Неотмодерированный юзер не может слать запросы
        if(!event.request_until) {
            setCanSend(true);
            return;
        }
        const today = Date.now() + 3 * 60 * 60 * 1000; // +3 часа
        const until = Date.parse(event.request_until);
        if(today <= until) setCanSend(true);
    }, [ user, event ]);

    // Для оформления формы готовим дату, время и длительность
    useEffect(() => {
        if(!slot) return;
        const [ hour, min ] = slot.start.split('T')[1].split(':');
        setDate(slot[date_loc]);
        setTime(`${hour}:${min}`);
        setDuration(slot.duration + duration_loc);
    }, [ slot, date_loc, duration_loc ]);

    // Отправка запроса
    const onSubmit = async () => {
		setIsLoading(true);
        setError('');
        setSuccess(false);

        const formData = new FormData();
        if(files.length > 0) formData.append('proposal', files[0], files[0].name);
        if(to) formData.append('to_id', to.id + '');
        if(slot) formData.append('slot_id', slot.id + '');
        formData.append('message_ru', message_ru);
        formData.append('message_en', message_en);

		const result = await post('/requests', formData);
		if(result.error) {
			setError(result.error.text[lang]);
		} 
        if(result.data) {
			dispatch(requestsAdd(result.data.request as Request));
            // У нас мог быть старый отмененный запрос с этим пользователем,
            // который мы получили в параметрах.
            // В случае успеха он был удален на сервере и нам локально тоже его
            // надо удалить чтобы не отображать в профайле в списке запросов.
			if(request) dispatch(requestsDel(request.id));
            setSuccess(true);
		}
        
        setIsLoading(false);
	};

    if(!slot || !to) return null;

	if(canSend) return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <h1>{t('request.title.post')}</h1>
            <div className='RequestPost'>
                <img alt='avatar' className='Avatar' src={getFileURL(to.avatar)}/>
                <div className='Info'>
                    <div className='Name'>{to[name]}</div>
                    <div>{date} {time} - {duration}</div>
                </div>
            </div>
            {success ? <p className='success'>{t('request.sucess_post')}</p> : <>
            {isLoading ? <p>{t('common.loading')}</p> :
            <Form
                error={error}
                submit={isTranslating ? undefined : t('request.submit')}
                onSubmit={onSubmit}
            >
                <Hidden id='to_id' value={to.id}/>
                <Hidden id='slot_id' value={slot.id}/>
                <Input
                    id='message_ru'
                    type='textarea'
                    value={message_ru}
                    onChange={setMessageRu}
                    label={t('request.message_ru')}
                    required={true}
                    placeholder={t('request.message_ru')}
                    limit={LIMIT}
                    disabled={isTranslating}
                    bottom={<Translate
                                current={message_ru}
                                text={message_en}
                                from='en'
                                limit={LIMIT}
                                setText={setMessageRu}
                                setIsTranslating={setIsTranslating}
                            />}
                />
                <Input
                    id='message_en'
                    type='textarea'
                    value={message_en}
                    onChange={setMessageEn}
                    label={t('request.message_en')}
                    required={true}
                    placeholder={t('request.message_en')}
                    limit={LIMIT}
                    disabled={isTranslating}
                    bottom={<Translate
                                current={message_en}
                                text={message_ru}
                                from='ru'
                                limit={LIMIT}
                                setText={setMessageEn}
                                setIsTranslating={setIsTranslating}
                            />}
                />
                <Fieldset
                    legend={t('request.proposal')}
                >
                    <Files
                        id='proposal'
                        label={t('request.proposal_select')}
                        files={files}
                        onChange={setFiles}
                        accept='.ppt,.pptx,.doc,.docx,.xls,.xlsx'
                        bottom={t('request.proposal_hint')}
                        disabled={isTranslating}
                    />
                </Fieldset>
            </Form>}
            </>}
        </Modal>
	);
    
    // Если нельзя отправлять запросы, выводим предупреждение в зависимости от причины
    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            {user?.is_approved ? <>
                <h1>{t('request.title.timeover')}</h1>
                <div
                    dangerouslySetInnerHTML={
                        {__html: t('request.timeover', { 'interpolation': {'escapeValue': false} })}
                    }
                />
            </> :
            <>
                <h1>{t('request.title.notapproved')}</h1>
                <div
                    dangerouslySetInnerHTML={
                        {__html: t('request.notapproved', { 'interpolation': {'escapeValue': false} })}
                    }
                />
            </>}
        </Modal>
    );
};

export default RequestPost;
